import React from 'react';
import { Link } from 'react-router-dom';
import { postApi, adApi } from '../utils/api';
import { Post, PaginatedResponse, Ad } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { MessageCircle, Eye, Heart, Pin, Award } from 'lucide-react';
import { getNicknameColor, getRoleLabel } from '../utils/roleUtils';
import { t } from '../stores/languageStore';
import AdDisplay from '../components/AdDisplay';

const Home: React.FC = () => {
  const [posts, setPosts] = React.useState<Post[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const [ads, setAds] = React.useState<Record<string, Ad[]>>({});
  const [adsLoading, setAdsLoading] = React.useState(true);

  const fetchPosts = React.useCallback(async () => {
    try {
      setLoading(true);
      const response: PaginatedResponse<Post> = await postApi.getPosts({ page, pageSize: 20 });
      if (response.success) {
        setPosts(response.data || []);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  const fetchAds = React.useCallback(async () => {
    try {
      setAdsLoading(true);
      const response = await adApi.getActiveAds();
      if (response.success && response.data) {
        setAds(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch ads:', error);
    } finally {
      setAdsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchPosts();
    fetchAds();
  }, [fetchPosts, fetchAds]);

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: zhCN });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* 顶部广告位 */}
      {!adsLoading && ads.home_top && ads.home_top.length > 0 && (
        <div className="mb-6">
          <AdDisplay ads={ads.home_top} />
        </div>
      )}

      {/* 帖子列表 */}
      <div className="space-y-4">
        {posts.map((post) => (
          <article key={post.id} className="card p-3 sm:p-4 hover:shadow-md transition-shadow">
            <div className="flex gap-3 sm:gap-4">
              {/* 作者头像 */}
              <Link to={`/user/${post.author_id}`} className="flex-shrink-0">
                <img
                  src={post.author_avatar || `https://ui-avatars.com/api/?name=${post.author_name}&background=3b82f6&color=fff`}
                  alt={post.author_name}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full"
                />
              </Link>

              {/* 帖子内容 */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {/* 标签 */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-1">
                      {post.is_top === 'global' && (
                        <span className="badge badge-danger flex items-center text-xs px-2 py-0.5">
                          <Pin className="w-2.5 h-2.5 mr-1" />
                          {t('posts.globalTop')}
                        </span>
                      )}
                      {post.is_top === 'category' && (
                        <span className="badge badge-warning flex items-center text-xs px-2 py-0.5">
                          <Pin className="w-2.5 h-2.5 mr-1" />
                          {t('posts.categoryTop')}
                        </span>
                      )}
                      {post.is_essence && (
                        <span className="badge badge-warning flex items-center text-xs px-2 py-0.5">
                          <Award className="w-2.5 h-2.5 mr-1" />
                          {t('posts.essence')}
                        </span>
                      )}
                      <Link
                        to={`/category/${post.category_id}`}
                        className="badge badge-primary text-xs px-2 py-0.5"
                      >
                        {post.category_name}
                      </Link>
                    </div>

                    {/* 标题 */}
                    <Link
                      to={`/post/${post.id}`}
                      className="text-base sm:text-lg font-semibold text-gray-900 hover:text-primary-600 line-clamp-1"
                    >
                      {post.title}
                    </Link>
                  </div>
                </div>

                {/* 元信息 */}
                <div className="flex items-center gap-2 sm:gap-3 mt-2 text-xs sm:text-sm text-gray-500 flex-wrap">
                  <Link
                    to={`/user/${post.author_id}`}
                    className={`hover:text-primary-600 ${getNicknameColor(post.author_role || '普通用户')}`}
                  >
                    {getRoleLabel(post.author_role || '普通用户')}{post.author_name}
                  </Link>
                  {post.author_level_icon && <span className="ml-1">{post.author_level_icon}</span>}
                  {post.author_titles && <span className="ml-1 text-xs text-purple-600">{post.author_titles}</span>}
                  <span className="text-xs text-gray-400">Lv.{post.author_level}</span>
                  <span>{formatDate(post.created_at)}</span>
                </div>

                {/* 统计信息 */}
                <div className="flex items-center gap-3 sm:gap-4 mt-2 sm:mt-3 text-xs sm:text-sm text-gray-500">
                  <span className="flex items-center">
                    <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                    {post.views}
                  </span>
                  <span className="flex items-center">
                    <MessageCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                    {post.replies_count}
                  </span>
                  <span className="flex items-center">
                    <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                    {post.likes}
                  </span>
                </div>

                {/* 最后回复 */}
                {post.last_reply_at && post.last_reply_user_name && (
                  <div className="mt-2 text-xs sm:text-sm text-gray-500">
                    {t('home.lastReply')}: <span className={getNicknameColor(post.last_reply_user_role || '普通用户')}>{getRoleLabel(post.last_reply_user_role || '普通用户')}{post.last_reply_user_name}</span>
                    {post.last_reply_user_level_icon && <span className="ml-1">{post.last_reply_user_level_icon}</span>}
                    · {formatDate(post.last_reply_at)}
                  </div>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* 分页 */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn btn-secondary disabled:opacity-50"
          >
            {t('home.previousPage')}
          </button>
          <span className="text-sm text-gray-600">
            {t('home.pageInfo', { page, totalPages: pagination.totalPages })}
          </span>
          <button
            onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
            disabled={page === pagination.totalPages}
            className="btn btn-secondary disabled:opacity-50"
          >
            {t('home.nextPage')}
          </button>
        </div>
      )}

      {/* 空状态 */}
      {posts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">{t('home.noPosts')}</p>
        </div>
      )}

      {/* 首页底部广告位 */}
      {!adsLoading && ads.home_bottom && ads.home_bottom.length > 0 && (
        <div className="mt-6">
          <AdDisplay ads={ads.home_bottom} />
        </div>
      )}
    </div>
  );
};

export default Home;
